import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from './TivigoDialog';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { purchaseTivigoPackage } from './creditCheckoutApi';
import { formatPackagePrice, loadPackagePricing, packageLabel } from './services/packagePricing';
import { formatCreditAmount, formatCreditShortfall, normalizeEurTryRate, packageCostTry } from './creditCurrency';
import { startDurationCouponConfetti } from './durationCouponConfetti';
import './userPanelDurationModal.css';

const USER_ONE_PERSON_PLANS = [
  { id: '3_months', duration: 3, label: '3 Aylık Paket', price: 700 },
  { id: '6_months', duration: 6, label: '6 Aylık Paket', price: 1100 },
  { id: '12_months', duration: 12, label: '12 Aylık Paket', price: 1400 },
  { id: '24_months', duration: 24, label: '24 Aylık Paket', price: 2300, popular: true },
  { id: '36_months', duration: 36, label: '36 Aylık Paket', price: 3900 },
  { id: '48_months', duration: 48, label: '48 Aylık Paket', price: 4900 },
  { id: 'unlimited', duration: 999, label: 'Sınırsız Paket', price: 5000 },
];

const USER_TWO_PERSON_PLANS = [
  { id: '2k_12_months', duration: 12, label: '12 Aylık Paket', price: 2300 },
  { id: '2k_24_months', duration: 24, label: '24 Aylık Paket', price: 4100, popular: true },
  { id: '2k_36_months', duration: 36, label: '36 Aylık Paket', price: 5700 },
  { id: '2k_48_months', duration: 48, label: '48 Aylık Paket', price: 7100 },
  { id: '2k_unlimited', duration: 999, label: 'Sınırsız Paket', price: 7000 },
];

const UPGRADE_MAP = {
  1: {
    '3_months': '12_months',
    '6_months': '12_months',
    '12_months': '24_months',
    '24_months': '36_months',
    '36_months': 'unlimited',
    '48_months': 'unlimited',
  },
  2: {
    '2k_12_months': '2k_24_months',
    '2k_24_months': '2k_36_months',
    '2k_36_months': '2k_48_months',
    '2k_48_months': '2k_unlimited',
  },
};

const formatMoney = (value) => `${Math.round(Number(value) || 0).toLocaleString('tr-TR')} TL`;

const getFirstNumber = (...values) => {
  for (const value of values) {
    if (value === '' || value == null) continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const parseExpiry = (value) => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDurationAccount = (userInfo, previewMode) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};
  const rawCredit = getFirstNumber(
    info.kredi,
    info.credit,
    info.credits,
    info.balance,
    user.kredi,
    user.credit,
    userInfo?.kredi,
  );
  const expiry = parseExpiry(info.exp_date ?? user.exp_date ?? userInfo?.exp_date);

  return {
    username: info.username || user.username || 'Tivigo kullanıcısı',
    credit: rawCredit == null && previewMode ? 5000 : rawCredit || 0,
    expiry: expiry && expiry.getTime() > Date.now() ? expiry : new Date(),
  };
};

const formatDate = (value) => {
  if (!value) return 'Bilgi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);
};

const projectExpiry = (currentExpiry, duration) => {
  if (duration === 999) return null;
  const baseDate = currentExpiry?.getTime() > Date.now()
    ? new Date(currentExpiry)
    : new Date();
  const currentDay = baseDate.getDate();

  baseDate.setDate(1);
  baseDate.setMonth(baseDate.getMonth() + duration);
  const lastDayOfTargetMonth = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth() + 1,
    0,
  ).getDate();
  baseDate.setDate(Math.min(currentDay, lastDayOfTargetMonth));

  return baseDate;
};

const getCouponIssue = (plan, tier, coupon) => {
  if (!plan || !coupon) return '';
  if (plan.duration === 3 || plan.duration === 6) {
    return 'Kupon ile minimum 12 aylık paket alabilirsiniz.';
  }
  if (coupon.onlyTwoPerson && Number(tier) !== 2) {
    return 'Bu kupon 2 kişilik paketler için geçerlidir.';
  }
  return '';
};

const normalizeCoupon = (value, fallbackCode) => {
  const source = value?.data || value || {};
  const amount = Number(source.tutar ?? source.amount ?? source.indirim_tutari);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Kupon indirim tutarı alınamadı.');
  }

  return {
    code: String(source.kod || source.code || fallbackCode || '').trim().toUpperCase(),
    amount,
    onlyTwoPerson: Boolean(source.sadece_2k ?? source.onlyTwoPerson),
  };
};

function UserPanelDurationModal({
  open,
  onClose,
  userInfo,
  previewMode = false,
  onPurchase,
  onValidateCoupon,
  onLoadAccount,
  onOpenCredit,
  onSuccess,
  isReseller = false,
  resellerDiscount = 0,
}) {
  const account = useMemo(() => getDurationAccount(userInfo, previewMode), [previewMode, userInfo]);
  const targetUserId = userInfo?._targetUserId || userInfo?.target_user_id || null;
  const resellerDiscountRate = isReseller && Number(resellerDiscount) > 0
    ? Number(resellerDiscount)
    : 0;
  // Bayi dahil fiyatlar sunucudaki güncel listeden gelir (bayi indirimi onun üzerinden düşülür);
  // yalnızca önizleme ve bayi olmayan hedefli akış sabit listeyi kullanır.
  const regionalPricing = !previewMode && (isReseller || !targetUserId);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState('');
  const [pricingLoading, setPricingLoading] = useState(false);
  const catalogPlans = pricing?.available ? pricing.plans.map((plan) => ({
    ...plan, label: packageLabel(plan.duration), popular: plan.duration === 24,
  })) : [];
  const onePersonPlans = regionalPricing ? catalogPlans.filter((plan) => plan.max_connections === 1) : USER_ONE_PERSON_PLANS;
  const twoPersonPlans = regionalPricing ? catalogPlans.filter((plan) => plan.max_connections === 2) : USER_TWO_PERSON_PLANS;
  const packageCurrency = regionalPricing ? (pricing?.available ? pricing.currency : null) : 'TRY';
  const eurTryRate = normalizeEurTryRate(pricing?.eur_try_rate);
  const formatPlanMoney = (value) => formatPackagePrice(value, packageCurrency);
  const formatBalance = (value) => formatCreditAmount(value, packageCurrency, eurTryRate, { maximumFractionDigits: 0 });
  const formatShortfall = (value) => formatCreditShortfall(value, packageCurrency, eurTryRate);
  const quoteTry = (price) => packageCostTry(price, packageCurrency, eurTryRate);
  const durationTierPanels = [
    { tier: 1, plans: onePersonPlans },
    { tier: 2, plans: twoPersonPlans },
  ];
  const [tier, setTier] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [dateAnimationKey, setDateAnimationKey] = useState(0);
  const [currentCredit, setCurrentCredit] = useState(previewMode ? account.credit : null);
  const [currentExpiry, setCurrentExpiry] = useState(account.expiry);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [result, setResult] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [upsellPlan, setUpsellPlan] = useState(null);
  const [creditWarningOpen, setCreditWarningOpen] = useState(false);
  const purchaseInFlightRef = useRef(false);
  const couponConfettiCleanupRef = useRef(null);
  const openingConfigRef = useRef({ account, targetUserId, previewMode, onLoadAccount });

  openingConfigRef.current = { account, targetUserId, previewMode, onLoadAccount };

  const plans = durationTierPanels.find((panel) => panel.tier === tier)?.plans || onePersonPlans;
  const activeTierIndex = Math.max(0, durationTierPanels.findIndex((panel) => panel.tier === tier));
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) || null;
  const activeCoupon = isReseller || packageCurrency !== 'TRY' ? null : coupon;
  const discountedPlanPrice = (plan) => {
    if (!plan) return 0;
    return resellerDiscountRate > 0
      ? plan.price - ((plan.price * resellerDiscountRate) / 100)
      : plan.price;
  };
  const selectedCouponIssue = getCouponIssue(selectedPlan, tier, activeCoupon);
  const selectedPrice = selectedPlan
    ? Math.max(0, discountedPlanPrice(selectedPlan) - (activeCoupon && !selectedCouponIssue ? activeCoupon.amount : 0))
    : 0;
  const conversionError = packageCurrency === 'EUR' && !eurTryRate
    ? 'Euro kuru alınamadı. Pencereyi yeniden açıp tekrar deneyin.' : '';
  const availabilityError = regionalPricing && pricing?.purchase_available === false
    ? 'Paket satın alma şu anda kullanılamıyor. Pencereyi yeniden açıp tekrar deneyin.' : '';
  const accountReady = currentCredit !== null && !creditLoading && !creditError;
  const pricingReady = !regionalPricing || (pricing?.available && ['TRY', 'EUR'].includes(packageCurrency) && !pricingLoading && !pricingError);
  const purchaseReady = accountReady && pricingReady && !conversionError && !availabilityError;
  const selectedCostTry = quoteTry(selectedPrice);
  const insufficientAmount = selectedPlan && purchaseReady && selectedCostTry !== null
    ? Math.max(0, selectedCostTry - currentCredit) : 0;
  const projectedExpiry = selectedPlan ? projectExpiry(currentExpiry, selectedPlan.duration) : null;
  const selectionIsValid = purchaseReady && !requestError && Boolean(selectedPlan) && selectedCostTry !== null && !selectedCouponIssue && insufficientAmount <= 0;

  useEffect(() => {
    if (!open || !regionalPricing) {
      setPricing(null);
      return undefined;
    }
    let cancelled = false;
    let pending = false;
    setPricing(null);
    setPricingError('');
    setPricingLoading(true);
    const refresh = () => {
      if (pending) return;
      pending = true;
      loadPackagePricing().then((catalog) => {
      if (cancelled) return;
      setPricing(catalog);
      setPricingError(catalog.available ? '' : 'Hesap tarifesi doğrulanamadı. Pencereyi yeniden açıp tekrar deneyin.');
    }).catch(() => {
      if (!cancelled) setPricingError('Paket fiyatları alınamadı. Pencereyi yeniden açıp tekrar deneyin.');
    }).finally(() => {
      pending = false;
      if (!cancelled) setPricingLoading(false);
    });
    };
    refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [open, regionalPricing]);

  useEffect(() => {
    if (!open) {
      setCreditWarningOpen(false);
      setCurrentCredit(null);
      setResult(null);
      return undefined;
    }

    const openingConfig = openingConfigRef.current;
    const openingAccount = openingConfig.account;
    const openingTargetUserId = openingConfig.targetUserId;
    const openingPreviewMode = openingConfig.previewMode;
    const openingLoadAccount = openingConfig.onLoadAccount;

    setTier(1);
    setSelectedPlanId('');
    setDateAnimationKey(0);
    setCurrentCredit(openingPreviewMode ? openingAccount.credit : null);
    setCurrentExpiry(openingAccount.expiry);
    setCreditLoading(false);
    setCreditError('');
    setLoading(false);
    setRequestError('');
    setResult(null);
    setCoupon(null);
    setCouponOpen(false);
    setCouponCode('');
    setCouponLoading(false);
    setCouponError('');
    setUpsellPlan(null);
    setCreditWarningOpen(false);
    purchaseInFlightRef.current = false;

    if (openingPreviewMode) return undefined;

    if (typeof openingLoadAccount !== 'function') {
      setCreditError('Hesap ve kredi bilgisi bağlantısı kullanılamıyor.');
      return undefined;
    }

    let cancelled = false;
    setCreditLoading(true);
    const accountRequest = openingTargetUserId
      ? Promise.all([
          Promise.resolve().then(() => openingLoadAccount({ targetUserId: openingTargetUserId })),
          Promise.resolve().then(() => openingLoadAccount({})),
        ])
      : Promise.resolve().then(() => openingLoadAccount({})).then((payload) => [payload, payload]);

    accountRequest
      .then(([targetPayload, ownerPayload]) => {
        if (cancelled) return;
        if (targetPayload?.success === false || ownerPayload?.success === false) throw new Error('Hesap ve kredi bilgisi alınamadı.');
        const targetUser = targetPayload?.data?.user || targetPayload?.user || {};
        const ownerUser = ownerPayload?.data?.user || ownerPayload?.user || {};
        const ownerCredit = getFirstNumber(
          ownerUser.kredi,
          ownerUser.credit,
          ownerUser.credits,
          ownerUser.balance,
        );
        if (ownerCredit == null) throw new Error('Mevcut kredi bilgisi alınamadı.');
        const targetExpiry = parseExpiry(targetUser.exp_date);
        setCurrentCredit(ownerCredit);
        setCurrentExpiry(targetExpiry?.getTime() > Date.now() ? targetExpiry : new Date());
      })
      .catch((error) => {
        if (!cancelled) setCreditError(error.message || 'Mevcut kredi bilgisi alınamadı.');
      })
      .finally(() => {
        if (!cancelled) setCreditLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    couponConfettiCleanupRef.current?.();
    couponConfettiCleanupRef.current = null;
  }, [open]);

  useEffect(() => () => {
    couponConfettiCleanupRef.current?.();
    couponConfettiCleanupRef.current = null;
  }, []);

  const chooseTier = (nextTier) => {
    setTier(nextTier);
    setSelectedPlanId('');
    setDateAnimationKey(0);
    setRequestError('');
    setCreditWarningOpen(false);
  };

  const choosePlan = (plan) => {
    const couponIssue = getCouponIssue(plan, tier, activeCoupon);
    const payablePrice = Math.max(
      0,
      discountedPlanPrice(plan) - (activeCoupon && !couponIssue ? activeCoupon.amount : 0),
    );
    setSelectedPlanId(plan.id);
    setDateAnimationKey((currentKey) => currentKey + 1);
    setRequestError('');
    setCreditWarningOpen(Boolean(purchaseReady && !couponIssue && quoteTry(payablePrice) > currentCredit));
  };

  const closeCouponDialog = () => {
    if (couponLoading) return;
    setCouponOpen(false);
    setCouponCode('');
    setCouponError('');
  };

  const applyCoupon = async (event) => {
    event.preventDefault();
    if (packageCurrency !== 'TRY') return;
    if (isReseller) {
      setCouponError('Bayi hesaplarında kupon yerine bayi indirimi uygulanır.');
      return;
    }
    const normalizedCode = couponCode.trim().toUpperCase();
    if (!normalizedCode) {
      setCouponError('Lütfen kupon kodu giriniz.');
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    try {
      const couponResult = previewMode
        ? normalizedCode === 'TIVIGO100'
          ? { kod: normalizedCode, tutar: 100, sadece_2k: false }
          : null
        : typeof onValidateCoupon === 'function'
          ? await onValidateCoupon({ code: normalizedCode, userInfo })
          : null;

      if (!couponResult) {
        throw new Error(previewMode
          ? 'Demo kupon kodu geçersiz. TIVIGO100 kodunu kullanın.'
          : 'Kupon doğrulama bağlantısı henüz sağlanmadı.');
      }

      setCoupon(normalizeCoupon(couponResult, normalizedCode));
      setCouponOpen(false);
      setCouponCode('');
      setSelectedPlanId('');
      setRequestError('');
      setCreditWarningOpen(false);
      couponConfettiCleanupRef.current?.();
      couponConfettiCleanupRef.current = startDurationCouponConfetti();
    } catch (error) {
      setCouponError(error.message || 'Kupon kodu doğrulanamadı.');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setSelectedPlanId('');
    setRequestError('');
    setCreditWarningOpen(false);
  };

  const getUpgradePlan = (plan) => {
    if (!plan) return null;
    const upgradeId = UPGRADE_MAP[tier]?.[plan.id];
    return plans.find((candidate) => candidate.id === upgradeId) || null;
  };

  const completePurchase = async (plan) => {
    if (!plan || purchaseInFlightRef.current || !purchaseReady || requestError) return;
    const couponIssue = getCouponIssue(plan, tier, activeCoupon);
    const expectedPrice = Math.max(0, discountedPlanPrice(plan) - (activeCoupon?.amount || 0));
    const expectedCostTry = quoteTry(expectedPrice);
    if (couponIssue || expectedCostTry === null || currentCredit < expectedCostTry) return;
    purchaseInFlightRef.current = true;
    setLoading(true);
    setRequestError('');

    try {
      if (activeCoupon && !previewMode && typeof onPurchase !== 'function') {
        throw new Error('Kuponlu satın alma için güvenli sunucu bağlantısı tamamlanmadan işlem yapılamaz.');
      }

      const purchaseResult = previewMode
        ? {
            new_credit: currentCredit - expectedCostTry,
            new_exp_date: plan.duration === 999
              ? Math.floor((Date.now() + (365 * 50 * 86400000)) / 1000)
              : Math.floor(projectExpiry(currentExpiry, plan.duration).getTime() / 1000),
            charged_amount: expectedCostTry,
            max_connections: tier,
          }
        : typeof onPurchase === 'function'
          ? await onPurchase({
              duration: plan.duration,
              maxConnections: tier,
              coupon: activeCoupon,
              expectedPrice,
              userInfo,
              ...(regionalPricing ? { pricing } : {}),
            })
          : await purchaseTivigoPackage({
              userInfo,
              duration: plan.duration,
              maxConnections: tier,
              ...(regionalPricing ? { pricing } : {}),
            });

      const newCredit = getFirstNumber(purchaseResult?.new_credit);
      const newExpiry = parseExpiry(purchaseResult?.new_exp_date) || projectedExpiry || currentExpiry;
      const normalizedResult = {
        ...purchaseResult,
        plan,
        preview: previewMode,
        newCredit: newCredit !== null ? newCredit : currentCredit - expectedCostTry,
        currency: purchaseResult?.currency || packageCurrency,
        eur_try_rate: purchaseResult?.eur_try_rate ?? eurTryRate,
        newExpiry,
      };

      setCurrentCredit(normalizedResult.newCredit);
      setCurrentExpiry(newExpiry);
      setResult(normalizedResult);
      setUpsellPlan(null);
      if (!previewMode) onSuccess?.(normalizedResult);
    } catch (error) {
      setRequestError(error.message || 'Süre ekleme işlemi tamamlanamadı.');
      setUpsellPlan(null);
    } finally {
      setLoading(false);
      purchaseInFlightRef.current = false;
    }
  };

  const handlePurchase = () => {
    if (!purchaseReady || requestError) return;
    if (!selectedPlan) {
      setRequestError('Lütfen bir paket seçin.');
      return;
    }
    if (selectedCouponIssue) {
      setRequestError(selectedCouponIssue);
      return;
    }
    if (insufficientAmount > 0) {
      setRequestError(`Yetersiz kredi! ${formatShortfall(insufficientAmount)} eksik.`);
      return;
    }

    const upgrade = getUpgradePlan(selectedPlan);
    if (upgrade && currentCredit < quoteTry(upgrade.price) && !targetUserId) {
      setUpsellPlan(upgrade);
      return;
    }
    completePurchase(selectedPlan);
  };

  const openCreditModal = () => {
    setUpsellPlan(null);
    setCreditWarningOpen(false);
    onClose?.();
    onOpenCredit?.({ currency: packageCurrency });
  };

  const handleExplicitClose = () => {
    couponConfettiCleanupRef.current?.();
    couponConfettiCleanupRef.current = null;
    setCreditWarningOpen(false);
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-duration-dialog"
        aria-labelledby="tvup-duration-modal-title"
        aria-describedby="tvup-duration-modal-description"
        slotProps={{
          backdrop: { className: 'tvup-duration-backdrop' },
          paper: { id: 'tvup-duration-modal', className: `tvup-duration-modal${result ? ' is-result' : ''}` },
        }}
      >
        <header className="tvup-duration-header">
          <div className="tvup-duration-title-icon" aria-hidden="true"><AccessTimeRoundedIcon /></div>
          <div className="tvup-duration-title-copy">
            <h2 id="tvup-duration-modal-title">Süre Ekle</h2>
            <p id="tvup-duration-modal-description">
              <span>{account.username}</span> için üyelik paketini seçin.
            </p>
          </div>
          <button type="button" className="tvup-duration-close" aria-label="Süre ekleme penceresini kapat" onClick={handleExplicitClose}>
            <CloseRoundedIcon />
          </button>
        </header>

        {result ? (
          <section className="tvup-duration-result" aria-live="polite">
            <span className="tvup-duration-result-icon"><CheckCircleRoundedIcon /></span>
            {result.preview ? <small>TASARIM ÖNİZLEMESİ</small> : null}
            <h3>{result.preview ? 'Paket seçimi hazır.' : 'Süreniz başarıyla uzatıldı.'}</h3>
            <p>
              {result.preview
                ? 'Canlı kullanıcı hesabında aynı seçim güvenli Tivigo sunucusu üzerinden uygulanır.'
                : `${result.plan.label} hesabınıza tanımlandı.`}
            </p>
            <div className="tvup-duration-result-facts">
              <div><span>Yeni bitiş</span><b>{result.plan.duration === 999 ? 'Sınırsız' : formatDate(result.newExpiry)}</b></div>
              <div><span>Kalan kredi</span><b>{formatCreditAmount(result.newCredit, result.currency, result.eur_try_rate, { maximumFractionDigits: 0 })}</b></div>
            </div>
          </section>
        ) : (
          <>
            <div className="tvup-duration-summary">
              {isReseller ? resellerDiscountRate > 0 ? (
                <div className="tvup-duration-coupon-applied">
                  <LocalOfferRoundedIcon />
                  <span><b>%{resellerDiscountRate} bayi indirimi</b></span>
                </div>
              ) : null : activeCoupon ? (
                <div className="tvup-duration-coupon-applied">
                  <LocalOfferRoundedIcon />
                  <span><b>{formatMoney(coupon.amount)} indirim</b><small>{coupon.code}</small></span>
                  <button type="button" aria-label="Kuponu kaldır" onClick={removeCoupon}><CloseRoundedIcon /></button>
                </div>
              ) : (
                <button type="button" className="tvup-duration-coupon-button" disabled={packageCurrency !== 'TRY'} title={packageCurrency !== 'TRY' ? 'Kuponlar bu para biriminde kullanılamaz.' : undefined} onClick={() => setCouponOpen(true)}>
                  <LocalOfferRoundedIcon /> Kupon Kodum Var
                </button>
              )}
              <div className="tvup-duration-credit">
                <span><small>Mevcut Krediniz</small><b>{creditLoading ? 'Kontrol ediliyor' : accountReady && pricingReady ? formatBalance(currentCredit) : '—'}</b></span>
                {creditLoading && <CircularProgress size={16} thickness={5} />}
              </div>
            </div>

            <div
              className="tvup-duration-tier-tabs"
              role="tablist"
              aria-label="Paket kişi sayısı"
              style={{
                '--tvup-duration-tier-x': tier === 2 ? 'calc(100% + 5px)' : '0px',
              }}
            >
              <button type="button" id="tvup-duration-tier-1-tab" role="tab" aria-controls="tvup-duration-tier-1-panel" aria-selected={tier === 1} className={tier === 1 ? 'is-active' : ''} onClick={() => chooseTier(1)}>
                <PersonOutlineRoundedIcon /> 1 Kişilik
              </button>
              <button type="button" id="tvup-duration-tier-2-tab" role="tab" aria-controls="tvup-duration-tier-2-panel" aria-selected={tier === 2} className={tier === 2 ? 'is-active' : ''} onClick={() => chooseTier(2)}>
                <GroupRoundedIcon /> 2 Kişilik
              </button>
            </div>

            <div className="tvup-duration-body" aria-busy={pricingLoading}>
              {pricingLoading && (
                <div className="tvup-duration-pricing-loading">
                  <CircularProgress size={24} thickness={4} disableShrink aria-label="Paket fiyatları yükleniyor" />
                </div>
              )}
              {pricingError && <Alert severity="error">{pricingError}</Alert>}
              <div
                className="tvup-duration-tier-track"
                style={{ transform: `translate3d(-${activeTierIndex * 100}%, 0, 0)` }}
              >
                {durationTierPanels.map(({ tier: panelTier, plans: panelPlans }) => (
                  <section
                    key={panelTier}
                    id={`tvup-duration-tier-${panelTier}-panel`}
                    className="tvup-duration-tier-slide"
                    role="tabpanel"
                    aria-labelledby={`tvup-duration-tier-${panelTier}-tab`}
                    aria-hidden={tier !== panelTier}
                  >
                    <div className="tvup-duration-plans">
                      {panelPlans.map((plan, index) => {
                        const isSelected = tier === panelTier && selectedPlanId === plan.id;
                        const couponIssue = getCouponIssue(plan, panelTier, activeCoupon);
                        const planPrice = discountedPlanPrice(plan);
                        const finalPrice = Math.max(0, planPrice - (activeCoupon && !couponIssue ? activeCoupon.amount : 0));
                        const shortfall = purchaseReady ? Math.max(0, quoteTry(finalPrice) - currentCredit) : 0;
                        const onePersonPlan = onePersonPlans.find((candidate) => candidate.duration === plan.duration);
                        const saving = panelTier > 1 && onePersonPlan
                          ? (onePersonPlan.price * panelTier) - plan.price
                          : 0;
                        const cardIssue = couponIssue || (shortfall > 0 ? `Yetersiz Kredi · ${formatShortfall(shortfall)} eksik` : '');

                        return (
                          <button
                            type="button"
                            key={plan.id}
                            tabIndex={tier === panelTier ? 0 : -1}
                            className={`tvup-duration-plan tone-${(index % 6) + 1}${isSelected ? ' is-selected' : ''}${isSelected && cardIssue ? ' has-issue' : ''}`}
                            aria-pressed={isSelected}
                            onClick={() => choosePlan(plan)}
                          >
                            {plan.popular && <span className="tvup-duration-popular">Popüler</span>}
                            <span className="tvup-duration-plan-check"><CheckCircleRoundedIcon /></span>
                            <b>{plan.label}</b>
                            <span className="tvup-duration-plan-price">
                              {(activeCoupon && !couponIssue) || resellerDiscountRate > 0
                                ? <><strong>{formatPlanMoney(finalPrice)}</strong><del>{formatPlanMoney(plan.price)}</del></>
                                : formatPlanMoney(plan.price)}
                            </span>
                            {saving > 0 && <small>1 kişilik pakete göre {formatPlanMoney(saving)} daha ucuz</small>}
                            {isSelected && cardIssue && <span className="tvup-duration-plan-issue">{cardIssue}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            <footer className="tvup-duration-footer">
              <div className={`tvup-duration-dates${selectedPlan ? ' has-selection' : ''}${selectedPlan && selectionIsValid ? ' is-ready' : ''}`}>
                <div className="tvup-duration-date-card is-current">
                  <small>Mevcut Bitiş</small>
                  <b>{formatDate(currentExpiry)}</b>
                </div>

                {selectedPlan && selectionIsValid && (
                  <span key={`duration-flow-${dateAnimationKey}`} className="tvup-duration-date-flow" aria-hidden="true">
                    <span className="tvup-duration-date-flow-motion">
                      <i>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </i>
                    </span>
                  </span>
                )}

                <div className="tvup-duration-date-card is-new" aria-live="polite" aria-atomic="true">
                  <div
                    key={`duration-date-${dateAnimationKey}`}
                    className={selectedPlan && selectionIsValid ? 'tvup-duration-date-copy is-animated' : 'tvup-duration-date-copy'}
                  >
                    <small>Yeni Bitiş</small>
                    <b>{selectedPlan ? selectedPlan.duration === 999 ? 'Sınırsız' : formatDate(projectedExpiry) : 'Paket seçin'}</b>
                  </div>
                </div>
              </div>

              {selectedCouponIssue && (
                <div className="tvup-duration-inline-warning">
                  <ErrorRoundedIcon />
                  <span>{selectedCouponIssue}</span>
                </div>
              )}
              {(creditError || conversionError || availabilityError || requestError) && <p className="tvup-duration-error" role="alert"><ErrorRoundedIcon /> {creditError || conversionError || availabilityError || requestError}</p>}

              <button type="button" className="tvup-duration-submit" disabled={!selectionIsValid || loading || creditLoading} onClick={handlePurchase}>
                {loading ? <><CircularProgress size={18} thickness={5} /> İşlem yapılıyor</> : <>Satın Al <ArrowForwardRoundedIcon /></>}
              </button>
            </footer>
          </>
        )}
      </Dialog>

      <Dialog
        open={couponOpen}
        maxWidth={false}
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-duration-subdialog"
        slotProps={{
          backdrop: { className: 'tvup-duration-subdialog-backdrop' },
          paper: { className: 'tvup-duration-coupon-modal' },
        }}
      >
        <header className="tvup-duration-subdialog-header">
          <span><LocalOfferRoundedIcon /></span>
          <div><h3>Kupon Kodu Kullan</h3><p>Kupon kodunu girin.</p></div>
          <button type="button" aria-label="Kupon penceresini kapat" onClick={closeCouponDialog}><CloseRoundedIcon /></button>
        </header>
        <form className="tvup-duration-coupon-form" onSubmit={applyCoupon}>
          <label htmlFor="tvup-duration-coupon-code">Kupon Kodu</label>
          <input
            id="tvup-duration-coupon-code"
            value={couponCode}
            disabled={couponLoading}
            autoFocus
            autoComplete="off"
            onChange={(event) => {
              setCouponCode(event.target.value.toUpperCase());
              setCouponError('');
            }}
          />
          {previewMode && <small className="tvup-duration-demo-coupon">Demo kodu: <b>TIVIGO100</b></small>}
          {couponError && <p role="alert"><ErrorRoundedIcon /> {couponError}</p>}
          <button type="submit" disabled={couponLoading || !couponCode.trim()}>
            {couponLoading ? <><CircularProgress size={16} thickness={5} /> Kontrol Ediliyor</> : 'Kupon Kodunu Uygula'}
          </button>
        </form>
      </Dialog>

      <Snackbar
        open={creditWarningOpen}
        transitionDuration={0}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="tvup-duration-credit-snackbar"
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<AccountBalanceWalletRoundedIcon />}
          className="tvup-duration-credit-alert"
          action={(
            <span className="tvup-duration-credit-alert-actions">
              <button type="button" onClick={openCreditModal}>Evet</button>
              <button type="button" onClick={() => setCreditWarningOpen(false)}>Hayır</button>
            </span>
          )}
        >
          Kredi yüklemek ister misiniz?
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(upsellPlan)}
        maxWidth={false}
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-duration-subdialog"
        slotProps={{
          backdrop: { className: 'tvup-duration-subdialog-backdrop' },
          paper: { className: 'tvup-duration-upsell-modal' },
        }}
      >
        {upsellPlan && selectedPlan && (
          <>
            <header className="tvup-duration-subdialog-header">
              <span><AccessTimeRoundedIcon /></span>
              <div><h3>Gözünüzden Kaçmış Olabilir!</h3><p>Daha avantajlı paketi değerlendirin.</p></div>
              <button type="button" aria-label="Öneri penceresini kapat" onClick={() => setUpsellPlan(null)}><CloseRoundedIcon /></button>
            </header>
            <div className="tvup-duration-upsell-body">
              <h4>{upsellPlan.duration === 999 ? 'Sınırsız kullanıma geçin' : `${upsellPlan.duration - selectedPlan.duration} ay daha fazla kullanın`}</h4>
              <p>Şu anda {formatBalance(currentCredit)} krediniz bulunuyor. {formatShortfall(Math.max(0, quoteTry(upsellPlan.price) - currentCredit))} daha yükleyerek önerilen paketten yararlanabilirsiniz.</p>
              <div className="tvup-duration-upsell-compare">
                <article><small>SEÇTİĞİNİZ</small><b>{selectedPlan.label}</b><span>{formatPlanMoney(selectedPlan.price)}</span><em>Aylık {formatPlanMoney(selectedPlan.price / selectedPlan.duration)}</em></article>
                <article><small>ÖNERİLEN</small><b>{upsellPlan.label}</b><span>{formatPlanMoney(upsellPlan.price)}</span><em>Aylık {formatPlanMoney(upsellPlan.price / upsellPlan.duration)}</em></article>
              </div>
              <button type="button" className="is-credit" onClick={openCreditModal}>Kredi Yükle ve {upsellPlan.label} Al</button>
              <button type="button" className="is-continue" disabled={!selectionIsValid || loading} onClick={() => completePurchase(selectedPlan)}>{selectedPlan.label} ile Devam Et</button>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
}

export default UserPanelDurationModal;
